// frontend/src/app/components/projects/project-corpus/dialogs/group-corpus-form.component.ts
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output, inject, signal } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';
import { ProjectCorpusService } from '../../../../project-corpus/domain/project-corpus.service';
import { GroupCorpusPaper, Page, PageRequest } from '../../../../project-corpus/domain/types';

const PAGE_SIZE = 10;
const DEBOUNCE_MS = 300;

/**
 * Group-corpus picker form for the Add-papers wizard.
 *
 * - Search input (debounced 300ms) → `service.searchGroupCorpus`.
 * - Paginated checkbox list. Rows where `alreadyInProject === true` show a
 *   muted "already in project" label and have their checkbox disabled.
 * - Sticky footer shows the count + an Add-to-project primary action.
 *
 * Responsive behaviour:
 *  - Search input takes full width on mobile and desktop.
 *  - Result list scrolls vertically inside `max-h-72 overflow-y-auto` so the
 *    rest of the dialog stays compact on phones.
 *  - The sticky footer is `flex-col` on mobile (counter above, action below
 *    full-width) and `sm:flex-row sm:justify-between` on desktop.
 */
@Component({
  selector: 'app-group-corpus-form',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './group-corpus-form.component.html',
  styleUrls: ['./group-corpus-form.component.scss'],
})
export class GroupCorpusFormComponent implements OnInit, OnDestroy {
  private readonly service = inject(ProjectCorpusService);

  readonly query = signal('');
  readonly results = signal<Page<GroupCorpusPaper> | null>(null);
  readonly loading = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly currentPageIndex = signal(0);
  readonly selectedIds = signal<readonly string[]>([]);
  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);

  @Output() done = new EventEmitter<void>();

  private readonly queryStream$ = new Subject<{ q: string; page: number }>();
  private readonly streamSub: Subscription;

  constructor() {
    this.streamSub = this.queryStream$
      .pipe(
        debounceTime(DEBOUNCE_MS),
        switchMap(({ q, page }) => {
          this.loading.set(true);
          this.loadError.set(null);
          const req: PageRequest = { page, size: PAGE_SIZE };
          return this.service.searchGroupCorpus(q, req);
        }),
      )
      .subscribe({
        next: (page) => {
          this.results.set(page);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loadError.set(this.toMessage(err));
          this.loading.set(false);
        },
      });
  }

  ngOnInit(): void {
    // Trigger an initial empty search so the user immediately sees results.
    // Done in ngOnInit (not the constructor) so the debounceTime timer is
    // scheduled in whatever zone the host is created in — under fakeAsync
    // tests, this lets `tick(350)` drain the initial call.
    this.queryStream$.next({ q: '', page: 0 });
  }

  ngOnDestroy(): void {
    this.streamSub.unsubscribe();
  }

  onQueryChange(value: string): void {
    this.query.set(value);
    this.currentPageIndex.set(0);
    this.queryStream$.next({ q: value.trim(), page: 0 });
  }

  goToPage(page: number): void {
    if (page < 0) return;
    this.currentPageIndex.set(page);
    this.queryStream$.next({ q: this.query().trim(), page });
  }

  isSelected(id: string): boolean {
    return this.selectedIds().includes(id);
  }

  toggle(paper: GroupCorpusPaper): void {
    if (paper.alreadyInProject) return;
    this.selectedIds.update((arr) =>
      arr.includes(paper.id) ? arr.filter((x) => x !== paper.id) : [...arr, paper.id],
    );
  }

  canPrev(): boolean {
    return this.currentPageIndex() > 0;
  }

  canNext(): boolean {
    const p = this.results();
    return !!p && this.currentPageIndex() < p.totalPages - 1;
  }

  totalPages(): number {
    return this.results()?.totalPages ?? 0;
  }

  submit(): void {
    if (this.submitting()) return;
    const ids = this.selectedIds();
    if (ids.length === 0) return;
    this.submitting.set(true);
    this.submitError.set(null);
    this.service.createImportJob({ source: 'GROUP_CORPUS', paperIds: [...ids] }).subscribe({
      next: () => {
        this.submitting.set(false);
        this.done.emit();
      },
      error: (err: unknown) => {
        this.submitError.set(this.toMessage(err));
        this.submitting.set(false);
      },
    });
  }

  private toMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
  }
}
